/**
 * LookBuilderScorer — Scoring local pour le constructeur de looks (Option A)
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Fournit 4 fonctions principales utilisées par CreateLookModal :
 *
 *   1. calculateLookScore(items, context)
 *      → { final_score, grade, breakdown }
 *      Calcule un score global 0-100 d'une tenue, avec breakdown par axe :
 *        • style       (StyleBank, items vs occasion)
 *        • color       (ColorBank + harmonie pair-wise)
 *        • material    (MaterialBank + pair-wise)
 *        • ai_vision   (moyenne des ai_metadata.coherence — défaut 80)
 *        • context     (météo + saison + occasion)
 *
 *   2. calculateCompatibility(item, currentLookItems, context)
 *      → { score: 0-160, badges, reasons }
 *      Score d'un item candidat pour compléter un look existant.
 *      Bonus +10 si source === 'wardrobe' (vs catalog).
 *
 *   3. generateLookNames(items, context, lang)
 *      → [5 suggestions]
 *      Génère 5 noms de looks via templates FR/EN.
 *
 *   4. validateLookName(name, existingNames)
 *      → { valid, error? }
 *      Valide la saisie utilisateur (length, mots interdits, unicité).
 *
 * 100% local — aucune dépendance réseau. Pondération finale :
 *   style*0.30 + color*0.25 + material*0.15 + ai_vision*0.20 + context*0.10
 *
 * Grade mapping :
 *   95-100 S+  🏆  Exceptionnel
 *   90-94  S   ⭐  Excellent
 *   85-89  A+  🎨  Très bon
 *   80-84  A   ✨  Bon
 *   70-79  B+  👍  Correct
 *   60-69  B   👌  Acceptable
 *   50-59  C   ⚠️   À retravailler
 *   0-49   D   ❌  Faible
 */

// ─────────────────────────────────────────────────────────────────────────
//  CONSTANTES DE STYLE / OCCASION / COULEUR
// ─────────────────────────────────────────────────────────────────────────

const STYLE_KEY_MAP = {
  chic: ['chic', 'élégant', 'elegant'],
  'élégant': ['chic', 'élégant', 'elegant'],
  classique: ['classique', 'minimaliste', 'chic'],
  casual: ['casual', 'décontracté'],
  'décontracté': ['casual', 'décontracté'],
  sport: ['sport', 'sportif'],
  sportif: ['sport', 'sportif'],
  streetwear: ['streetwear', 'street', 'urban', 'edgy'],
  minimaliste: ['minimaliste', 'minimal'],
  romantique: ['romantique', 'féminin'],
  bohème: ['bohème', 'boheme', 'bohemian'],
  workwear: ['workwear', 'business', 'preppy', 'chic'],
};

const OCCASION_KEY_MAP = {
  Casual:     ['quotidien', 'weekend'],
  Bureau:     ['bureau'],
  'Soirée':   ['soirée', 'cocktail'],
  Sport:      ['sport'],
  'Cérémonie':['cérémonie', 'cocktail'],
  'Week-end': ['weekend', 'quotidien'],
  Brunch:     ['weekend', 'date', 'quotidien'],
  Date:       ['date', 'cocktail'],
};

const NEUTRAL_COLORS = new Set([
  'noir','black','blanc','white','beige','gris','grey','gray',
  'écru','ecru','crème','creme','ivoire','taupe','sable','champagne',
]);

const COLOR_FAMILY_MAP = {
  vert:    ['vert','kaki','olive','menthe','sapin','émeraude','pistache','amande','anis','bouteille'],
  bleu:    ['bleu','marine','denim','ciel','azur','pétrole','turquoise','indigo','klein'],
  rouge:   ['rouge','bordeaux','framboise','rouille','cerise','tomate','coquelicot','brique'],
  rose:    ['rose','fuchsia','saumon','blush','poudré','dragée'],
  marron:  ['marron','cognac','chocolat','caramel','tabac','moka','terracotta','camel','châtain'],
  beige:   ['beige','sable','écru','champagne','taupe','crème','ivoire','ficelle','lin','nude'],
  gris:    ['gris','souris','perle','anthracite','ardoise','étain','argent'],
  jaune:   ['jaune','moutarde','citron','ocre','safran','paille','doré','miel'],
  violet:  ['violet','lavande','prune','lilas','aubergine','parme','mauve'],
  orange:  ['orange','corail','abricot','mandarine','cuivré'],
  noir:    ['noir','charbon','jais'],
  blanc:   ['blanc','ivoire','neige','optique'],
};

function getColorFamily(color) {
  if (!color) return null;
  const c = String(color).toLowerCase().trim();
  for (const [family, members] of Object.entries(COLOR_FAMILY_MAP)) {
    if (members.some(m => c.includes(m) || m.includes(c))) return family;
  }
  return null;
}

const MATERIAL_FAMILIES = {
  casual: ['coton','denim','jean','jersey','lin','maille','sweat','molleton','chambray'],
  chic:   ['soie','cachemire','laine','satin','velours','cuir','daim','suède','nubuck','tweed','gabardine','mohair','organza','crepe','crêpe','alpaga','angora'],
  sport:  ['polyester','polyamide','néoprène','neoprene','élasthanne','elasthanne','mesh','nylon','lycra','spandex'],
};

function getMaterialFamily(material) {
  if (!material) return null;
  const m = String(material).toLowerCase().trim();
  if (!m) return null;
  for (const [family, list] of Object.entries(MATERIAL_FAMILIES)) {
    if (list.some(token => m.includes(token) || token.includes(m))) return family;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────
//  HELPERS — STYLE / COULEUR / MATIÈRE / CONTEXTE
// ─────────────────────────────────────────────────────────────────────────

/**
 * Score "style" d'un item face à l'occasion (0-100).
 * Combine styleScores AI + occasions AI.
 */
function scoreItemStyle(item, occasion) {
  const ai = item?.ai_metadata || {};
  const styleScores = ai.styleScores || {};
  const occScores = ai.occasions || {};

  // Pas de data du tout → neutre 60
  if (Object.keys(styleScores).length === 0 && Object.keys(occScores).length === 0) {
    return 60;
  }

  // Occasion match (priorité haute)
  let occScore = 50;
  if (occasion) {
    const occKeys = OCCASION_KEY_MAP[occasion] || [String(occasion).toLowerCase()];
    let best = 0;
    for (const k of occKeys) {
      const v = occScores[k] || 0;
      if (v > best) best = v;
    }
    if (best > 0) occScore = best;
  }

  // Style "expressivité" (max des styleScores connus) — un item bien défini
  // stylistiquement est plus utile qu'un item flou.
  const styleVals = Object.values(styleScores).filter(v => typeof v === 'number');
  const styleMax = styleVals.length > 0 ? Math.max(...styleVals) : 50;

  // 60% occasion / 40% style expressivity
  return Math.round(occScore * 0.6 + styleMax * 0.4);
}

/**
 * Score "color" via ColorBank (si dispo) + harmonie pair-wise.
 */
function scoreItemColorLocal(item, otherItems, colorBank) {
  let bankScore = 60; // neutre
  if (colorBank?.byKey) {
    try {
      const { scoreItemColor } = require('./ColorBankService');
      const r = scoreItemColor(item, colorBank);
      if (typeof r?.score === 'number') bankScore = Math.round(r.score * 100);
    } catch {}
  }

  // Harmonie pair-wise contre les autres items
  let harmonyScore = 70;
  if (otherItems.length > 0) {
    const ai = item.ai_metadata || {};
    const itemPrimary = String(ai.primaryColor || ai.mainColor || item.color || '').toLowerCase();
    const itemFamily = getColorFamily(itemPrimary);
    const isNeutral = NEUTRAL_COLORS.has(itemPrimary) ||
                      Array.from(NEUTRAL_COLORS).some(n => itemPrimary && itemPrimary.includes(n));

    let pairs = 0;
    let total = 0;
    for (const o of otherItems) {
      const oAi = o.ai_metadata || {};
      const oPrimary = String(oAi.primaryColor || oAi.mainColor || o.color || '').toLowerCase();
      if (!oPrimary) continue;
      const oFamily = getColorFamily(oPrimary);
      const oNeutral = NEUTRAL_COLORS.has(oPrimary) ||
                       Array.from(NEUTRAL_COLORS).some(n => oPrimary.includes(n));
      let pair = 60;
      if (itemPrimary === oPrimary) pair = 90;
      else if (itemFamily && oFamily && itemFamily === oFamily) pair = 85;
      else if (isNeutral || oNeutral) pair = 80;
      else pair = 50;
      total += pair;
      pairs++;
    }
    if (pairs > 0) harmonyScore = Math.round(total / pairs);
  }

  // 50% bank / 50% harmony pair-wise
  return Math.round(bankScore * 0.5 + harmonyScore * 0.5);
}

/**
 * Score "material" via MaterialBank (si dispo) + harmonie pair-wise.
 */
function scoreItemMaterialLocal(item, otherItems, materialBank) {
  let bankScore = 60;
  if (materialBank?.byKey) {
    try {
      const { scoreItemMaterial } = require('./MaterialBankService');
      const r = scoreItemMaterial(item, materialBank);
      if (typeof r?.score === 'number') bankScore = Math.round(r.score * 100);
    } catch {}
  }

  // Pair-wise par famille (casual/chic/sport)
  let pairScore = 70;
  const mat = (item.ai_metadata?.material || '').toLowerCase().trim();
  const fam = getMaterialFamily(mat);
  if (mat && otherItems.length > 0) {
    let pairs = 0;
    let total = 0;
    for (const o of otherItems) {
      const oMat = (o.ai_metadata?.material || '').toLowerCase().trim();
      if (!oMat) continue;
      const oFam = getMaterialFamily(oMat);
      let pair;
      if (mat === oMat) pair = 95;
      else if (fam && oFam && fam === oFam) pair = 90;
      else if (!fam || !oFam) pair = 70;
      else {
        const combo = [fam, oFam].sort().join('+');
        const compat = { 'casual+chic': 75, 'casual+sport': 65, 'chic+sport': 35 };
        pair = compat[combo] ?? 70;
      }
      total += pair;
      pairs++;
    }
    if (pairs > 0) pairScore = Math.round(total / pairs);
  }

  return Math.round(bankScore * 0.5 + pairScore * 0.5);
}

/**
 * Score "ai_vision" — moyenne des ai_metadata.coherence des items.
 * Si coherence absent → 80 par défaut (modéré-haut, neutre positif).
 */
function scoreAiVision(items) {
  if (!items || items.length === 0) return 80;
  const vals = items
    .map(it => {
      const c = it?.ai_metadata?.coherence;
      if (typeof c === 'number') return c > 1 ? c : c * 100; // tolère 0-1 ou 0-100
      return null;
    })
    .filter(v => v !== null);
  if (vals.length === 0) return 80;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

/**
 * Score "context" — météo (température) + saison + occasion alignée.
 */
function scoreContext(items, context = {}) {
  if (!items || items.length === 0) return 70;
  const tempC = typeof context.tempC === 'number' ? context.tempC : null;
  const season = (context.season || '').toLowerCase().trim();
  const occasion = context.occasion;

  // Weather (40%)
  let weather = 80;
  if (tempC !== null) {
    let penalty = 0;
    for (const it of items) {
      const range = it?.ai_metadata?.temperatureRange;
      if (!Array.isArray(range) || range.length !== 2) continue;
      const [mn, mx] = range;
      if (typeof mn !== 'number' || typeof mx !== 'number') continue;
      if (mn === 0 && mx === 0) continue; // pas calculé
      if (tempC >= mn && tempC <= mx) continue;
      if (tempC >= mn - 3 && tempC <= mx + 3) penalty += 5;
      else if (tempC >= mn - 8 && tempC <= mx + 8) penalty += 15;
      else penalty += 25;
    }
    weather = Math.max(0, 100 - penalty);
  }

  // Season (30%)
  let seasonScore = 80;
  if (season && season !== 'toutes') {
    let aligned = 0;
    let total = 0;
    for (const it of items) {
      const s = it?.ai_metadata?.seasonality;
      if (!Array.isArray(s) || s.length === 0) continue;
      total++;
      const seasons = s.map(x => String(x).toLowerCase());
      const ok = seasons.includes('toutes') || seasons.some(x => x.includes(season) || season.includes(x));
      if (ok) aligned++;
    }
    if (total > 0) seasonScore = Math.round((aligned / total) * 100);
  }

  // Occasion alignment (30%) — moyenne des occasion-scores
  let occScore = 75;
  if (occasion) {
    const occKeys = OCCASION_KEY_MAP[occasion] || [String(occasion).toLowerCase()];
    let total = 0;
    let count = 0;
    for (const it of items) {
      const occs = it?.ai_metadata?.occasions || {};
      if (Object.keys(occs).length === 0) continue;
      const best = Math.max(...occKeys.map(k => occs[k] || 0));
      total += best;
      count++;
    }
    if (count > 0) occScore = Math.round(total / count);
  }

  return Math.round(weather * 0.4 + seasonScore * 0.3 + occScore * 0.3);
}

// ─────────────────────────────────────────────────────────────────────────
//  GRADE
// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
//  DIAGNOSTICS — Identifie les paires problématiques par axe
// ─────────────────────────────────────────────────────────────────────────

/**
 * Trouve les paires d'items dont la matière est incompatible.
 * Retourne { issues: [{ a, b, aMat, bMat, aFam, bFam, score, severity }] }
 */
export function diagnoseMaterialConflicts(items) {
  const out = { issues: [], missingData: [] };
  if (!items || items.length < 2) return out;
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i];
      const b = items[j];
      const aMat = (a?.ai_metadata?.material || '').toLowerCase().trim();
      const bMat = (b?.ai_metadata?.material || '').toLowerCase().trim();
      if (!aMat || !bMat) {
        if (!aMat) out.missingData.push({ name: a?.name || a?.brand || '?', missing: 'material' });
        if (!bMat) out.missingData.push({ name: b?.name || b?.brand || '?', missing: 'material' });
        continue;
      }
      const aFam = getMaterialFamily(aMat);
      const bFam = getMaterialFamily(bMat);
      let score = 70;
      let label = null;
      if (aMat === bMat) { score = 95; }
      else if (aFam && bFam && aFam === bFam) { score = 90; }
      else if (!aFam || !bFam) { score = 70; }
      else {
        const combo = [aFam, bFam].sort().join('+');
        const compat = { 'casual+chic': 75, 'casual+sport': 65, 'chic+sport': 35 };
        score = compat[combo] ?? 70;
        label = combo;
      }
      if (score < 70) {
        out.issues.push({
          a: a?.name || a?.brand || '?',
          b: b?.name || b?.brand || '?',
          aMat, bMat,
          aFam: aFam || 'inconnu',
          bFam: bFam || 'inconnu',
          score,
          severity: score < 50 ? 'high' : 'medium',
          combo: label,
        });
      }
    }
  }
  return out;
}

/**
 * Trouve les paires de couleurs problématiques.
 */
export function diagnoseColorConflicts(items) {
  const out = { issues: [], missingData: [] };
  if (!items || items.length < 2) return out;
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i];
      const b = items[j];
      const aAi = a?.ai_metadata || {};
      const bAi = b?.ai_metadata || {};
      const aCol = String(aAi.primaryColor || aAi.mainColor || a?.color || '').toLowerCase();
      const bCol = String(bAi.primaryColor || bAi.mainColor || b?.color || '').toLowerCase();
      if (!aCol || !bCol) {
        if (!aCol) out.missingData.push({ name: a?.name || a?.brand || '?', missing: 'color' });
        if (!bCol) out.missingData.push({ name: b?.name || b?.brand || '?', missing: 'color' });
        continue;
      }
      const aFam = getColorFamily(aCol);
      const bFam = getColorFamily(bCol);
      const aN = NEUTRAL_COLORS.has(aCol) || Array.from(NEUTRAL_COLORS).some(n => aCol.includes(n));
      const bN = NEUTRAL_COLORS.has(bCol) || Array.from(NEUTRAL_COLORS).some(n => bCol.includes(n));
      let score = 60;
      if (aCol === bCol) score = 90;
      else if (aFam && bFam && aFam === bFam) score = 85;
      else if (aN || bN) score = 80;
      else score = 50;
      if (score < 70) {
        out.issues.push({
          a: a?.name || a?.brand || '?',
          b: b?.name || b?.brand || '?',
          aCol, bCol,
          aFam: aFam || null,
          bFam: bFam || null,
          score,
          severity: score < 55 ? 'high' : 'medium',
        });
      }
    }
  }
  return out;
}

/**
 * Trouve les items dont le style ne match pas l'occasion choisie.
 */
export function diagnoseStyleConflicts(items, occasion) {
  const out = { issues: [], missingData: [] };
  if (!items || items.length === 0 || !occasion) return out;
  const occKeys = OCCASION_KEY_MAP[occasion] || [String(occasion).toLowerCase()];
  for (const it of items) {
    const ai = it?.ai_metadata || {};
    const occs = ai.occasions || {};
    if (Object.keys(occs).length === 0) {
      out.missingData.push({ name: it?.name || it?.brand || '?', missing: 'occasion_tags' });
      continue;
    }
    const best = Math.max(...occKeys.map(k => occs[k] || 0));
    if (best < 50) {
      // Trouver l'occasion DOMINANTE de l'item
      let topOcc = null;
      let topVal = 0;
      for (const [k, v] of Object.entries(occs)) {
        if (typeof v === 'number' && v > topVal) { topVal = v; topOcc = k; }
      }
      out.issues.push({
        item: it?.name || it?.brand || '?',
        wantedOccasion: occasion,
        actualOccasion: topOcc || 'inconnu',
        wantedScore: best,
        actualScore: topVal,
        severity: best < 30 ? 'high' : 'medium',
      });
    }
  }
  return out;
}

function gradeFor(score) {
  if (score >= 95) return { grade: 'S+', icon: '🏆', labelKey: 'grade_exceptional' };
  if (score >= 90) return { grade: 'S',  icon: '⭐', labelKey: 'grade_excellent' };
  if (score >= 85) return { grade: 'A+', icon: '🎨', labelKey: 'grade_very_good' };
  if (score >= 80) return { grade: 'A',  icon: '✨', labelKey: 'grade_good' };
  if (score >= 70) return { grade: 'B+', icon: '👍', labelKey: 'grade_decent' };
  if (score >= 60) return { grade: 'B',  icon: '👌', labelKey: 'grade_acceptable' };
  if (score >= 50) return { grade: 'C',  icon: '⚠️',  labelKey: 'grade_needs_work' };
  return                  { grade: 'D',  icon: '❌', labelKey: 'grade_poor' };
}

// ─────────────────────────────────────────────────────────────────────────
//  API 1 — calculateLookScore
// ─────────────────────────────────────────────────────────────────────────

/**
 * Calcule le score global d'un look (0-100) avec breakdown par axe.
 *
 * @param {Array} items Items du look (dressing + suggestions sélectionnées)
 * @param {Object} context { occasion, season, tempC, colorBank, materialBank }
 * @returns {{
 *   final_score: number,
 *   grade: { grade, icon, labelKey },
 *   breakdown: Object  // {style, color, material, ai_vision, context}
 * }}
 */
export function calculateLookScore(items, context = {}) {
  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];
  if (safeItems.length === 0) {
    return {
      final_score: 0,
      grade: gradeFor(0),
      breakdown: {},
      items_score: [],
    };
  }

  const { occasion, colorBank, materialBank } = context;

  // ─── Calcul par item ───
  const perItem = safeItems.map(it => {
    const others = safeItems.filter(o => o !== it && o?.id !== it?.id);
    return {
      id: it.id,
      name: it.name || it.brand || '',
      category: it.category || '',
      style: scoreItemStyle(it, occasion),
      color: scoreItemColorLocal(it, others, colorBank),
      material: scoreItemMaterialLocal(it, others, materialBank),
    };
  });

  // ─── Moyennes par axe ───
  const avg = (key) => perItem.length === 0 ? 60
    : Math.round(perItem.reduce((a, p) => a + p[key], 0) / perItem.length);

  const styleAvg     = avg('style');
  const colorAvg     = avg('color');
  const materialAvg  = avg('material');
  const aiVisionAvg  = scoreAiVision(safeItems);
  const contextAvg   = scoreContext(safeItems, context);

  // ─── Pondération finale (Option A) ───
  const W = { style: 0.30, color: 0.25, material: 0.15, ai_vision: 0.20, context: 0.10 };
  const final =
      styleAvg    * W.style
    + colorAvg    * W.color
    + materialAvg * W.material
    + aiVisionAvg * W.ai_vision
    + contextAvg  * W.context;

  const finalRounded = Math.max(0, Math.min(100, Math.round(final)));

  // v1.12.268 — Diagnostics : identifie les paires/items problématiques
  const styleDiag    = diagnoseStyleConflicts(safeItems, occasion);
  const colorDiag    = diagnoseColorConflicts(safeItems);
  const materialDiag = diagnoseMaterialConflicts(safeItems);

  // ─── Breakdown détaillé ───
  const breakdown = {
    style: {
      score: styleAvg,
      weighted: Math.round(styleAvg * W.style),
      labelKey: 'score_style_match',
      icon: '🎯',
      weight: W.style,
      details: perItem.map(p => `${p.name}: ${p.style}/100`),
      issues: styleDiag.issues,
      missingData: styleDiag.missingData,
    },
    color: {
      score: colorAvg,
      weighted: Math.round(colorAvg * W.color),
      labelKey: 'score_color_harmony',
      icon: '🎨',
      weight: W.color,
      details: perItem.map(p => `${p.name}: ${p.color}/100`),
      issues: colorDiag.issues,
      missingData: colorDiag.missingData,
    },
    material: {
      score: materialAvg,
      weighted: Math.round(materialAvg * W.material),
      labelKey: 'score_material_compat',
      icon: '🧵',
      weight: W.material,
      details: perItem.map(p => `${p.name}: ${p.material}/100`),
      issues: materialDiag.issues,
      missingData: materialDiag.missingData,
    },
    ai_vision: {
      score: aiVisionAvg,
      weighted: Math.round(aiVisionAvg * W.ai_vision),
      labelKey: 'score_ai_vision',
      icon: '👁️',
      weight: W.ai_vision,
      details: [`${aiVisionAvg}/100`],
    },
    context: {
      score: contextAvg,
      weighted: Math.round(contextAvg * W.context),
      labelKey: 'score_context',
      icon: '🌤️',
      weight: W.context,
      details: [
        context.occasion ? `Occasion: ${context.occasion}` : null,
        context.season ? `Season: ${context.season}` : null,
        typeof context.tempC === 'number' ? `Temp: ${context.tempC}°C` : null,
      ].filter(Boolean),
    },
  };

  return {
    final_score: finalRounded,
    grade: gradeFor(finalRounded),
    breakdown,
    items_score: perItem,
  };
}

// ─────────────────────────────────────────────────────────────────────────
//  API 2 — calculateCompatibility
// ─────────────────────────────────────────────────────────────────────────

/**
 * Score de compatibilité d'un item candidat avec un look en cours.
 * Échelle 0-160 (la composante user prefs/budget/coherence peut amener au-delà
 * de 100). Le total nominal est : 40 + 30 + 20 + 15 + 15 + 15 + 15 + 10 = 160.
 *
 * @param {Object} item Candidat (dressing ou catalogue)
 * @param {Array}  currentLookItems Items déjà dans le look
 * @param {Object} context { occasion, season, tempC, userPrefs, budget, colorBank, materialBank }
 * @returns {{ score: number, badges: Array, reasons: Array }}
 */
export function calculateCompatibility(item, currentLookItems = [], context = {}) {
  if (!item) return { score: 0, badges: [], reasons: [] };

  const badges = [];
  const reasons = [];

  // ─── Color harmony (max 40) ───
  const colorRaw = scoreItemColorLocal(item, currentLookItems, context.colorBank);
  const colorPoints = Math.round((colorRaw / 100) * 40);
  if (colorRaw >= 85) {
    badges.push({ key: 'harmony', icon: '🎨', labelKey: 'badge_color_harmony' });
    reasons.push({ axis: 'color', score: colorRaw });
  }

  // ─── Style (max 30) ───
  const styleRaw = scoreItemStyle(item, context.occasion);
  const stylePoints = Math.round((styleRaw / 100) * 30);
  if (styleRaw >= 80) {
    badges.push({ key: 'style', icon: '✨', labelKey: 'badge_style_match' });
    reasons.push({ axis: 'style', score: styleRaw });
  }

  // ─── Occasion (max 20) ───
  let occRaw = 50;
  const occ = context.occasion;
  if (occ) {
    const keys = OCCASION_KEY_MAP[occ] || [String(occ).toLowerCase()];
    const occs = item?.ai_metadata?.occasions || {};
    const best = Math.max(...keys.map(k => occs[k] || 0));
    if (best > 0) occRaw = best;
  }
  const occPoints = Math.round((occRaw / 100) * 20);

  // ─── User prefs (max 15) ───
  let userPrefsPoints = 7;
  const prefs = context.userPrefs || {};
  if (prefs.favoriteColors && Array.isArray(prefs.favoriteColors)) {
    const itemColor = String(item.ai_metadata?.primaryColor || item.color || '').toLowerCase();
    if (prefs.favoriteColors.some(c => String(c).toLowerCase() === itemColor)) {
      userPrefsPoints = 15;
      badges.push({ key: 'fav_color', icon: '💖', labelKey: 'badge_fav_color' });
    }
  }

  // ─── Weather (max 15) ───
  let weatherPoints = 10;
  if (typeof context.tempC === 'number') {
    const range = item?.ai_metadata?.temperatureRange;
    if (Array.isArray(range) && range.length === 2) {
      const [mn, mx] = range;
      if (typeof mn === 'number' && typeof mx === 'number' && !(mn === 0 && mx === 0)) {
        if (context.tempC >= mn && context.tempC <= mx) weatherPoints = 15;
        else if (context.tempC >= mn - 3 && context.tempC <= mx + 3) weatherPoints = 12;
        else if (context.tempC >= mn - 8 && context.tempC <= mx + 8) weatherPoints = 7;
        else weatherPoints = 2;
      }
    }
  }

  // ─── Budget (max 15) ───
  let budgetPoints = 10;
  if (typeof context.budget === 'number' && context.budget > 0) {
    const price = parseFloat(item.price) || 0;
    if (price <= 0) budgetPoints = 15; // dressing (gratuit) — bonus
    else if (price <= context.budget * 0.5) budgetPoints = 15;
    else if (price <= context.budget) budgetPoints = 12;
    else if (price <= context.budget * 1.5) budgetPoints = 5;
    else budgetPoints = 0;
  } else if (!item.price || parseFloat(item.price) <= 0) {
    budgetPoints = 15; // dressing
  }

  // ─── Coherence / AI vision (max 15) ───
  let coherenceRaw = item?.ai_metadata?.coherence;
  if (typeof coherenceRaw === 'number' && coherenceRaw <= 1) coherenceRaw *= 100;
  if (typeof coherenceRaw !== 'number') coherenceRaw = 80;
  const coherencePoints = Math.round((coherenceRaw / 100) * 15);

  // ─── BONUS WARDROBE (+10) ───
  const isWardrobe = item.source === 'wardrobe'
    || item._source === 'wardrobe'
    || (!item.affiliateUrl && !item.link && !item.url && item.image_url);
  const wardrobeBonus = isWardrobe ? 10 : 0;
  if (isWardrobe) {
    badges.push({ key: 'wardrobe', icon: '👗', labelKey: 'badge_from_wardrobe' });
  }

  const score = colorPoints + stylePoints + occPoints + userPrefsPoints
              + weatherPoints + budgetPoints + coherencePoints + wardrobeBonus;

  return {
    score,
    breakdown: {
      color:    colorPoints,
      style:    stylePoints,
      occasion: occPoints,
      user:     userPrefsPoints,
      weather:  weatherPoints,
      budget:   budgetPoints,
      coherence: coherencePoints,
      wardrobe_bonus: wardrobeBonus,
    },
    badges,
    reasons,
  };
}

// ─────────────────────────────────────────────────────────────────────────
//  API 3 — generateLookNames
// ─────────────────────────────────────────────────────────────────────────

const STYLE_DISPLAY = {
  fr: {
    chic: 'Chic', 'élégant': 'Élégant', classique: 'Classique',
    casual: 'Casual', 'décontracté': 'Décontracté',
    sport: 'Sport', sportif: 'Sportif', streetwear: 'Streetwear',
    minimaliste: 'Minimaliste', romantique: 'Romantique', 'bohème': 'Bohème',
    workwear: 'Workwear', preppy: 'Preppy', edgy: 'Edgy',
  },
  en: {
    chic: 'Chic', 'élégant': 'Elegant', classique: 'Classic',
    casual: 'Casual', 'décontracté': 'Casual',
    sport: 'Sport', sportif: 'Sporty', streetwear: 'Streetwear',
    minimaliste: 'Minimal', romantique: 'Romantic', 'bohème': 'Boho',
    workwear: 'Workwear', preppy: 'Preppy', edgy: 'Edgy',
  },
};

const OCCASION_DISPLAY = {
  fr: {
    Casual: 'Casual', 'Soirée': 'Soirée', Bureau: 'Bureau',
    Sport: 'Sport', 'Cérémonie': 'Cérémonie', 'Week-end': 'Week-end',
    Brunch: 'Brunch', Date: 'Date',
  },
  en: {
    Casual: 'Casual', 'Soirée': 'Evening', Bureau: 'Office',
    Sport: 'Sport', 'Cérémonie': 'Ceremony', 'Week-end': 'Weekend',
    Brunch: 'Brunch', Date: 'Date',
  },
};

const SEASON_DISPLAY = {
  fr: {
    printemps: 'printanier', été: 'estival', automne: 'automnal',
    hiver: 'hivernal', toutes: 'intemporel',
  },
  en: {
    printemps: 'Spring', été: 'Summer', automne: 'Autumn',
    hiver: 'Winter', toutes: 'Timeless',
  },
};

const ADJECTIVES = {
  fr: {
    chic:        ['raffiné', 'élégant', 'sophistiqué'],
    casual:      ['détendu', 'décontracté', 'cool'],
    sport:       ['dynamique', 'énergique', 'actif'],
    romantique:  ['romantique', 'délicat', 'féminin'],
    streetwear:  ['urbain', 'street', 'audacieux'],
    minimaliste: ['épuré', 'minimal', 'pur'],
    'bohème':    ['bohème', 'libre', 'aérien'],
    workwear:    ['affirmé', 'pro', 'structuré'],
  },
  en: {
    chic:        ['Refined', 'Elegant', 'Sophisticated'],
    casual:      ['Relaxed', 'Easy', 'Cool'],
    sport:       ['Dynamic', 'Energetic', 'Active'],
    romantique:  ['Romantic', 'Delicate', 'Feminine'],
    streetwear:  ['Urban', 'Street', 'Bold'],
    minimaliste: ['Minimal', 'Pure', 'Clean'],
    'bohème':    ['Boho', 'Free', 'Airy'],
    workwear:    ['Sharp', 'Pro', 'Structured'],
  },
};

function detectDominantStyle(items) {
  if (!items || items.length === 0) return null;
  const totals = {};
  for (const it of items) {
    const ss = it?.ai_metadata?.styleScores || {};
    for (const [k, v] of Object.entries(ss)) {
      if (typeof v !== 'number') continue;
      totals[k] = (totals[k] || 0) + v;
    }
  }
  let best = null, bestV = -1;
  for (const [k, v] of Object.entries(totals)) {
    if (v > bestV) { bestV = v; best = k; }
  }
  return best;
}

function detectDominantColor(items) {
  if (!items || items.length === 0) return null;
  const counts = {};
  for (const it of items) {
    const c = (it?.ai_metadata?.primaryColor || it?.color || '').toLowerCase();
    if (!c) continue;
    counts[c] = (counts[c] || 0) + 1;
  }
  let best = null, bestN = 0;
  for (const [c, n] of Object.entries(counts)) {
    if (n > bestN) { bestN = n; best = c; }
  }
  return best;
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Génère 5 suggestions de noms pour le look.
 * Templates :
 *   FR : "{Style} {Occasion} {Couleur}"
 *   FR : "Look {adjectif} {saison}"
 *   FR : "Tenue {style} pour {occasion}"
 *   EN : "{Color} {Occasion} {Style}"
 *   EN : "{Style} {Season} Look"
 *   EN : "{Style} {Occasion} Outfit"
 *
 * @param {Array} items Items du look
 * @param {Object} context { occasion, season }
 * @param {string} lang 'fr' | 'en'
 * @returns {string[]} 5 suggestions
 */
export function generateLookNames(items, context = {}, lang = 'fr') {
  const L = lang === 'en' ? 'en' : 'fr';
  const styleKey = detectDominantStyle(items) || 'chic';
  const colorKey = detectDominantColor(items) || (L === 'fr' ? 'beige' : 'Beige');
  const season = (context.season || '').toLowerCase().trim();

  const styleDisp = STYLE_DISPLAY[L][styleKey] || capitalize(styleKey);
  const occDisp   = OCCASION_DISPLAY[L][context.occasion] || (context.occasion || (L === 'fr' ? 'Quotidien' : 'Everyday'));
  const colorDisp = capitalize(colorKey);
  const seasonDisp = SEASON_DISPLAY[L][season] || (L === 'fr' ? 'intemporel' : 'Timeless');
  const adjPool = ADJECTIVES[L][styleKey] || ADJECTIVES[L]['chic'];
  const adjDisp = adjPool[Math.floor(Math.random() * adjPool.length)];

  let suggestions;
  if (L === 'fr') {
    suggestions = [
      `${styleDisp} ${occDisp} ${colorDisp}`,
      `Look ${adjDisp} ${seasonDisp}`,
      `Tenue ${styleDisp.toLowerCase()} pour ${occDisp.toLowerCase()}`,
      `${colorDisp} ${styleDisp.toLowerCase()} ${seasonDisp}`,
      `${occDisp} ${styleDisp} ${colorDisp.toLowerCase()}`,
    ];
  } else {
    suggestions = [
      `${colorDisp} ${occDisp} ${styleDisp}`,
      `${styleDisp} ${seasonDisp} Look`,
      `${styleDisp} ${occDisp} Outfit`,
      `${adjDisp} ${occDisp} Look`,
      `${colorDisp} ${seasonDisp} ${styleDisp}`,
    ];
  }

  // Nettoyer espaces multiples + capitalize first letter
  return suggestions.map(s => s.replace(/\s+/g, ' ').trim()).filter(Boolean).slice(0, 5);
}

// ─────────────────────────────────────────────────────────────────────────
//  API 4 — validateLookName
// ─────────────────────────────────────────────────────────────────────────

const FORBIDDEN_WORDS = ['test', 'draft', 'undefined', 'null', 'brouillon'];

/**
 * Valide un nom de look saisi par l'utilisateur.
 *
 * Règles :
 *  - longueur 3-50 caractères
 *  - non vide / pas uniquement espaces
 *  - pas uniquement des chiffres
 *  - aucun mot interdit (test, draft, etc.)
 *  - unique parmi les existingNames (insensible à la casse)
 *
 * @param {string} name
 * @param {string[]} existingNames
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateLookName(name, existingNames = []) {
  if (typeof name !== 'string') return { valid: false, error: 'name_required' };
  const trimmed = name.trim();
  if (trimmed.length === 0) return { valid: false, error: 'name_required' };
  if (trimmed.length < 3) return { valid: false, error: 'name_too_short' };
  if (trimmed.length > 50) return { valid: false, error: 'name_too_long' };
  if (/^\d+$/.test(trimmed)) return { valid: false, error: 'name_only_digits' };

  const lower = trimmed.toLowerCase();
  for (const w of FORBIDDEN_WORDS) {
    if (lower === w || lower.includes(w)) {
      return { valid: false, error: 'name_forbidden_word' };
    }
  }

  if (Array.isArray(existingNames)) {
    const existsLower = existingNames.map(n => String(n || '').trim().toLowerCase());
    if (existsLower.includes(lower)) return { valid: false, error: 'name_already_used' };
  }

  return { valid: true };
}

// ─────────────────────────────────────────────────────────────────────────
//  HELPER public — détecte les items "forts" / "faibles" pour breakdown UI
// ─────────────────────────────────────────────────────────────────────────

/**
 * Retourne les "strengths" (items > 80) et "weaknesses" (items < 70) à partir
 * d'un résultat de calculateLookScore.
 */
export function extractStrengthsWeaknesses(scoreResult) {
  const itemsScore = scoreResult?.items_score || [];
  const enriched = itemsScore.map(it => ({
    ...it,
    avg: Math.round((it.style + it.color + it.material) / 3),
  }));
  const strengths = enriched.filter(it => it.avg >= 80)
    .sort((a, b) => b.avg - a.avg).slice(0, 3);
  const weaknesses = enriched.filter(it => it.avg < 70)
    .sort((a, b) => a.avg - b.avg).slice(0, 3);
  return { strengths, weaknesses };
}

export default {
  calculateLookScore,
  calculateCompatibility,
  generateLookNames,
  validateLookName,
  extractStrengthsWeaknesses,
};
